import React, { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext';
import { OrganizerProfile } from '../../types/Common';
import { getOrganizerProfile, stripeOnboarding, stripeAccountLink, getStripeAccountStatus } from '../../api';
import { StripeAccountStatusResponse } from '../../types/StripeAccountStatusResponse';

 

const Payout = () => {
    const { user } = useAuth();
    document.title = "Payouts | EventoX";
    const [organizerDetails, setOrganizerDetails] = useState<OrganizerProfile | null>(null);
    const [stripeStatus, setStripeStatus] = useState<StripeAccountStatusResponse | null>(null);
    const [isActivating, setIsActivating] = useState(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch organizer details and Stripe account status
    useEffect(() => {
        if (user) {
            const fetchData = async () => {
                try {
                    // Fetch organizer details
                    const organizerDetailsData = await getOrganizerProfile();
                    console.log("Organizer Details Data:", organizerDetailsData);
                    setOrganizerDetails(organizerDetailsData);

                    // If Stripe account exists, check its status
                    if (organizerDetailsData?.stripeAccountId) {
                        setIsLoadingStatus(true);
                        try {
                            const statusData: any = await getStripeAccountStatus();
                            console.log("Stripe Account Status:", statusData);
                            setStripeStatus(statusData);
                        } catch (statusError) {
                            console.error("Error fetching Stripe status:", statusError);
                            // Don't set error here, just log it
                        } finally {
                            setIsLoadingStatus(false);
                        }
                    }
                } catch (error) {
                    console.error("Error fetching organizer details:", error);
                    setError("Failed to load organizer details");
                }
            }
            fetchData();
        }
    }, [user]);

    // Handle Stripe activation
    const handleActivateStripe = async () => {
        setIsActivating(true);
        setError(null);
        
        try {
            // Step 1: Create Stripe account (onboarding)
            const onboardingResponse: { accountId?: string } = await stripeOnboarding();
            console.log("Stripe onboarding response:", onboardingResponse);
            
            if (!onboardingResponse?.accountId) {
                throw new Error("Failed to create Stripe account");
            }

            // Step 2: Get account link for onboarding
            const accountLinkResponse: { link?: string } = await stripeAccountLink(onboardingResponse.accountId);
            console.log("Stripe account link response:", accountLinkResponse);
            
            if (!accountLinkResponse?.link) {
                throw new Error("Failed to get Stripe onboarding link");
            }

            // Step 3: Redirect user to Stripe onboarding page
            window.location.href = accountLinkResponse.link;
            
        } catch (error: any) {
            console.error("Error activating Stripe:", error);
            setError(error?.response?.data?.message || error?.message || "Failed to activate Stripe. Please try again.");
            setIsActivating(false);
        }
    };

    // Determine Stripe account status
    const getAccountStatus = () => {
        if (!organizerDetails?.stripeAccountId) {
            return 'not_setup';
        }
        if (isLoadingStatus || !stripeStatus) {
            return 'checking';
        }
        const requirements = (stripeStatus as any).requirements;
        if (requirements?.currently_due?.length === 0 && stripeStatus.account.capabilities.transfers === 'active') {
            return 'fully_active';
        }
        if (requirements?.currently_due?.length > 0 || stripeStatus.account.capabilities.transfers === 'active') {
            return 'partially_active';
        }
        return 'incomplete';
    };

    const accountStatus = getAccountStatus();

    // Render status card based on account status
    const renderStatusCard = () => {
        switch (accountStatus) {
            case 'not_setup':
                return {
                    icon: 'fa-solid fa-circle-exclamation',
                    title: 'Stripe Connect Not Set Up',
                    message: 'You need to set up your Stripe Connect account to receive payouts from ticket sales.',
                    bgColor: '#fff3cd',
                    borderColor: '#ffc107',
                    textColor: '#856404',
                    iconColor: '#ffc107',
                    showAction: true
                };
            case 'checking':
                return {
                    icon: 'fa-solid fa-spinner fa-spin',
                    title: 'Checking Account Status',
                    message: 'Please wait while we verify your Stripe account status...',
                    bgColor: '#e7f3ff',
                    borderColor: '#0d6efd',
                    textColor: '#084298',
                    iconColor: '#0d6efd',
                    showAction: false
                };
            case 'fully_active':
                return {
                    icon: 'fa-solid fa-circle-check',
                    title: 'Stripe Connect Fully Active',
                    message: 'Your account is fully set up and ready to accept payments. You can now sell tickets and receive payouts.',
                    bgColor: '#d4edda',
                    borderColor: '#28a745',
                    textColor: '#155724',
                    iconColor: '#28a745',
                    showAction: false
                };
            case 'partially_active':
                return {
                    icon: 'fa-solid fa-triangle-exclamation',
                    title: 'Account Setup Incomplete',
                    message: 'Your Stripe account is partially set up. Please complete the onboarding process to enable ticket sales.',
                    bgColor: '#fff3cd',
                    borderColor: '#ffc107',
                    textColor: '#856404',
                    iconColor: '#ffc107',
                    showAction: true
                };
            case 'incomplete':
                return {
                    icon: 'fa-solid fa-clock',
                    title: 'Onboarding In Progress',
                    message: 'Your Stripe account setup is in progress. Please complete all required steps to enable payments.',
                    bgColor: '#fff3cd',
                    borderColor: '#ffc107',
                    textColor: '#856404',
                    iconColor: '#ffc107',
                    showAction: true
                };
            default:
                return null;
        }
    };

    const statusCard = renderStatusCard();

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            <h1 style={{ marginBottom: '30px', fontSize: '32px', fontWeight: '600', color: '#333' }}>
                Payout Settings
            </h1>
            
            {/* Stripe Account Status Card */}
            {statusCard && (
                <div style={{ 
                    padding: '30px', 
                    marginBottom: '30px', 
                    backgroundColor: statusCard.bgColor, 
                    border: `2px solid ${statusCard.borderColor}`,
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '20px'
                }}>
                    {/* Icon */}
                    <div style={{
                        fontSize: '48px',
                        color: statusCard.iconColor,
                        flexShrink: 0
                    }}>
                        <i className={statusCard.icon}></i>
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                        <h2 style={{ 
                            margin: '0 0 10px 0', 
                            color: statusCard.textColor,
                            fontSize: '24px',
                            fontWeight: '600'
                        }}>
                            {statusCard.title}
                        </h2>
                        <p style={{ 
                            margin: '0 0 20px 0', 
                            color: statusCard.textColor,
                            fontSize: '16px',
                            lineHeight: '1.6'
                        }}>
                            {statusCard.message}
                        </p>

                        {/* Show detailed status if account exists */}
                        {stripeStatus && accountStatus !== 'checking' && (
                            <div style={{
                                marginTop: '15px',
                                padding: '15px',
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '15px',
                                    color: statusCard.textColor
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {(() => {
                                            const requirements = (stripeStatus as any).requirements;
                                            const currentlyDue = requirements?.currently_due?.length || 0;
                                            return (
                                                <>
                                                    <i className={`fa-solid ${currentlyDue === 0 ? 'fa-circle-check' : 'fa-circle-xmark'}`}
                                                       style={{ color: currentlyDue === 0 ? '#28a745' : '#dc3545' }}></i>
                                                    <span>Details Submitted: {currentlyDue === 0 ? 'Yes' : 'No'}</span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className={`fa-solid ${stripeStatus.account.capabilities.transfers === 'active' ? 'fa-circle-check' : 'fa-circle-xmark'}`}
                                           style={{ color: stripeStatus.account.capabilities.transfers === 'active' ? '#28a745' : '#dc3545' }}></i>
                                        <span>Charges Enabled: {stripeStatus.account.capabilities.transfers === 'active' ? 'Yes' : 'No'}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Button */}
                        {statusCard.showAction && (
                            <div style={{ marginTop: '20px' }}>
                                <button
                                    onClick={handleActivateStripe}
                                    disabled={isActivating}
                                    style={{
                                        padding: '12px 30px',
                                        backgroundColor: statusCard.iconColor,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        cursor: isActivating ? 'not-allowed' : 'pointer',
                                        opacity: isActivating ? 0.7 : 1,
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onMouseOver={(e) => {
                                        if (!isActivating) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                                    }}
                                >
                                    {isActivating ? (
                                        <>
                                            <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                            Activating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-link" style={{ marginRight: '8px' }}></i>
                                            {accountStatus === 'not_setup' ? 'Set Up Stripe Connect' : 'Complete Setup'}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div style={{ 
                                marginTop: '15px',
                                padding: '12px',
                                backgroundColor: '#f8d7da',
                                border: '1px solid #dc3545',
                                borderRadius: '6px',
                                color: '#721c24',
                                fontSize: '14px'
                            }}>
                                <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '8px' }}></i>
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Detailed Account Information */}
            {stripeStatus && accountStatus !== 'checking' && (
                <div style={{ marginTop: '30px' }}>
                    <h2 style={{ 
                        marginBottom: '20px', 
                        fontSize: '24px', 
                        fontWeight: '600', 
                        color: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <i className="fa-solid fa-circle-info" style={{ color: '#0d6efd' }}></i>
                        Complete Account Details
                    </h2>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                        gap: '20px'
                    }}>
                        {/* Account Overview */}
                        <DetailCard
                            title="Account Overview"
                            icon="fa-solid fa-building"
                            iconColor="#0d6efd"
                        >
                            <DetailRow label="Account ID" value={stripeStatus.account.id || 'N/A'} />
                            <DetailRow label="Object Type" value={stripeStatus.account.object} />
                            <DetailRow label="Business Type" value={stripeStatus.account.business_type || 'N/A'} />
                            <DetailRow 
                                label="Charges Enabled" 
                                value={stripeStatus.account.charges_enabled ? 'Yes' : 'No'}
                                valueColor={stripeStatus.account.charges_enabled ? '#28a745' : '#dc3545'}
                            />
                            <DetailRow 
                                label="Payouts Enabled" 
                                value={(stripeStatus as any).payouts_enabled ? 'Yes' : 'No'}
                                valueColor={(stripeStatus as any).payouts_enabled ? '#28a745' : '#dc3545'}
                            />
                            {/* <DetailRow label="Company Name" value={stripeStatus.account.company.name || 'N/A'} /> */}
                        </DetailCard>

                        {/* Capabilities */}
                        <DetailCard
                            title="Capabilities"
                            icon="fa-solid fa-check-circle"
                            iconColor="#28a745"
                        >
                            <DetailRow 
                                label="Card Payments" 
                                value={stripeStatus.account.capabilities.card_payments || 'N/A'}
                                valueColor={stripeStatus.account.capabilities.card_payments === 'active' ? '#28a745' : '#ffc107'}
                            />
                            <DetailRow 
                                label="Transfers" 
                                value={stripeStatus.account.capabilities.transfers || 'N/A'}
                                valueColor={stripeStatus.account.capabilities.transfers === 'active' ? '#28a745' : '#ffc107'}
                            />
                        </DetailCard>

                        {/* Business Profile */}
                        <DetailCard
                            title="Business Profile"
                            icon="fa-solid fa-briefcase"
                            iconColor="#6f42c1"
                        >
                            <DetailRow label="Business Name" value={stripeStatus.account.business_profile.name || 'N/A'} />
                            <DetailRow label="URL" value={stripeStatus.account.business_profile.url || 'N/A'} />
                            <DetailRow label="MCC Code" value={stripeStatus.account.business_profile.mcc || 'N/A'} />
                            <DetailRow label="Support Email" value={stripeStatus.account.business_profile.support_email || 'N/A'} />
                            <DetailRow label="Support Phone" value={stripeStatus.account.business_profile.support_phone || 'N/A'} />
                            <DetailRow label="Support URL" value={stripeStatus.account.business_profile.support_url || 'N/A'} />
                            {stripeStatus.account.business_profile.annual_revenue && (
                                <DetailRow label="Annual Revenue" value={`$${stripeStatus.account.business_profile.annual_revenue.toLocaleString()}`} />
                            )}
                            {stripeStatus.account.business_profile.estimated_worker_count && (
                                <DetailRow label="Estimated Workers" value={stripeStatus.account.business_profile.estimated_worker_count.toString()} />
                            )}
                        </DetailCard>

                        {/* Requirements Status */}
                        {(() => {
                            const requirements = (stripeStatus as any).requirements;
                            const currentlyDue = requirements?.currently_due?.length || 0;
                            return (
                                <DetailCard
                                    title="Requirements Status"
                                    icon="fa-solid fa-clipboard-check"
                                    iconColor={currentlyDue === 0 ? '#28a745' : '#ffc107'}
                                >
                                    <DetailRow 
                                        label="Currently Due" 
                                        value={currentlyDue === 0 ? 'None' : `${currentlyDue} items`}
                                        valueColor={currentlyDue === 0 ? '#28a745' : '#dc3545'}
                                    />
                                    {currentlyDue > 0 && requirements?.currently_due && (
                                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px' }}>
                                            <strong style={{ display: 'block', marginBottom: '8px', color: '#856404' }}>Required Items:</strong>
                                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404', fontSize: '13px' }}>
                                                {requirements.currently_due.map((item: string, index: number) => (
                                                    <li key={index}>{item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {requirements?.past_due && (
                                        <DetailRow 
                                            label="Past Due" 
                                            value={requirements.past_due.length === 0 ? 'None' : `${requirements.past_due.length} items`}
                                            valueColor={requirements.past_due.length === 0 ? '#28a745' : '#dc3545'}
                                        />
                                    )}
                                    {requirements?.eventually_due && (
                                        <DetailRow 
                                            label="Eventually Due" 
                                            value={requirements.eventually_due.length === 0 ? 'None' : `${requirements.eventually_due.length} items`}
                                        />
                                    )}
                                    {requirements?.current_deadline && (
                                        <DetailRow label="Current Deadline" value={new Date(requirements.current_deadline).toLocaleDateString()} />
                                    )}
                                    {requirements?.disabled_reason && (
                                        <DetailRow label="Disabled Reason" value={requirements.disabled_reason} valueColor="#dc3545" />
                                    )}
                                    {requirements?.alternatives && requirements.alternatives.length > 0 && (
                                        <DetailRow label="Alternatives" value={`${requirements.alternatives.length} alternatives available`} />
                                    )}
                                    {requirements?.errors && requirements.errors.length > 0 && (
                                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8d7da', borderRadius: '6px' }}>
                                            <strong style={{ display: 'block', marginBottom: '8px', color: '#721c24' }}>Errors:</strong>
                                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#721c24', fontSize: '13px' }}>
                                                {requirements.errors.map((error: string, index: number) => (
                                                    <li key={index}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </DetailCard>
                            );
                        })()}

                        {/* Individual Information */}
                        {(stripeStatus as any).individual && (
                            <DetailCard
                                title="Individual Information"
                                icon="fa-solid fa-user"
                                iconColor="#17a2b8"
                            >
                                <DetailRow label="Email" value={(stripeStatus as any).individual.email || 'N/A'} />
                                <DetailRow label="First Name" value={(stripeStatus as any).individual.first_name || 'N/A'} />
                                <DetailRow label="Last Name" value={(stripeStatus as any).individual.last_name || 'N/A'} />
                                {(stripeStatus as any).individual.relationship && (
                                    <>
                                        <DetailRow label="Owner" value={(stripeStatus as any).individual.relationship.owner ? 'Yes' : 'No'} />
                                        <DetailRow label="Director" value={(stripeStatus as any).individual.relationship.director ? 'Yes' : 'No'} />
                                        <DetailRow label="Executive" value={(stripeStatus as any).individual.relationship.executive ? 'Yes' : 'No'} />
                                        {(stripeStatus as any).individual.relationship.percent_ownership && (
                                            <DetailRow label="Ownership %" value={`${(stripeStatus as any).individual.relationship.percent_ownership}%`} />
                                        )}
                                        <DetailRow label="Title" value={(stripeStatus as any).individual.relationship.title || 'N/A'} />
                                    </>
                                )}
                            </DetailCard>
                        )}

                        {/* Controller Information */}
                        {stripeStatus.account.controller && (
                            <DetailCard
                                title="Controller Information"
                                icon="fa-solid fa-user-shield"
                                iconColor="#fd7e14"
                            >
                                <DetailRow label="Is Controller" value={stripeStatus.account.controller.is_controller ? 'Yes' : 'No'} />
                                <DetailRow label="Fees Payer" value={stripeStatus.account.controller.fees.payer || 'N/A'} />
                                <DetailRow label="Losses Payments" value={stripeStatus.account.controller.losses.payments || 'N/A'} />
                                <DetailRow label="Account Holder Name" value={stripeStatus.account.controller.account_holder_name || 'N/A'} />
                                <DetailRow label="Dashboard Type" value={stripeStatus.account.controller.stripe_dashboard.type || 'N/A'} />
                                <DetailRow label="Requirement Collection" value={stripeStatus.account.controller.requirement_collection || 'N/A'} />
                                {stripeStatus.account.controller.last4 && (
                                    <DetailRow label="Last 4 Digits" value={stripeStatus.account.controller.last4} />
                                )}
                                {stripeStatus.account.controller.routing_number && (
                                    <DetailRow label="Routing Number" value={stripeStatus.account.controller.routing_number} />
                                )}
                                <DetailRow label="Status" value={stripeStatus.account.controller.status || 'N/A'} />
                            </DetailCard>
                        )}

                        {/* Payment Settings */}
                        {(stripeStatus as any).settings && (
                            <DetailCard
                                title="Payment Settings"
                                icon="fa-solid fa-credit-card"
                                iconColor="#20c997"
                            >
                                {(stripeStatus as any).settings.card_payments && (
                                    <>
                                        <DetailRow label="Display Name" value={(stripeStatus as any).settings.card_payments.dashboard.display_name || 'N/A'} />
                                        <DetailRow label="Timezone" value={(stripeStatus as any).settings.card_payments.dashboard.timezone || 'N/A'} />
                                        {(stripeStatus as any).settings.card_payments.payments && (
                                            <>
                                                <DetailRow label="Statement Descriptor" value={(stripeStatus as any).settings.card_payments.payments.statement_descriptor || 'N/A'} />
                                                {(stripeStatus as any).settings.card_payments.payouts && (
                                                    <>
                                                        <DetailRow 
                                                            label="Debit Negative Balances" 
                                                            value={(stripeStatus as any).settings.card_payments.payouts.debit_negative_balances ? 'Yes' : 'No'} 
                                                        />
                                                        {(stripeStatus as any).settings.card_payments.payouts.schedule && (
                                                            <>
                                                                <DetailRow label="Payout Delay Days" value={(stripeStatus as any).settings.card_payments.payouts.schedule.delay_days?.toString() || 'N/A'} />
                                                                <DetailRow label="Payout Interval" value={(stripeStatus as any).settings.card_payments.payouts.schedule.interval || 'N/A'} />
                                                            </>
                                                        )}
                                                        <DetailRow label="Payout Statement Descriptor" value={(stripeStatus as any).settings.card_payments.payouts.statement_descriptor || 'N/A'} />
                                                    </>
                                                )}
                                            </>
                                        )}
                                        {(stripeStatus as any).settings.card_payments.decline_on && (
                                            <>
                                                <DetailRow label="Decline on AVS Failure" value={(stripeStatus as any).settings.card_payments.decline_on.avs_failure ? 'Yes' : 'No'} />
                                                <DetailRow label="Decline on CVC Failure" value={(stripeStatus as any).settings.card_payments.decline_on.cvc_failure ? 'Yes' : 'No'} />
                                            </>
                                        )}
                                    </>
                                )}
                            </DetailCard>
                        )}

                        {/* Branding Settings */}
                        {(stripeStatus as any).settings?.branding && (
                            <DetailCard
                                title="Branding"
                                icon="fa-solid fa-palette"
                                iconColor="#e83e8c"
                            >
                                <DetailRow label="Icon" value={(stripeStatus as any).settings.branding.icon ? 'Set' : 'Not Set'} />
                                <DetailRow label="Logo" value={(stripeStatus as any).settings.branding.logo ? 'Set' : 'Not Set'} />
                                <DetailRow label="Primary Color" value={(stripeStatus as any).settings.branding.primary_color || 'N/A'} />
                                <DetailRow label="Secondary Color" value={(stripeStatus as any).settings.branding.secondary_color || 'N/A'} />
                            </DetailCard>
                        )}

                        {/* Terms of Service */}
                        {(stripeStatus as any).settings?.tos_acceptance && (
                            <DetailCard
                                title="Terms of Service"
                                icon="fa-solid fa-file-contract"
                                iconColor="#6c757d"
                            >
                                <DetailRow 
                                    label="Accepted Date" 
                                    value={(stripeStatus as any).settings.tos_acceptance.date ? new Date((stripeStatus as any).settings.tos_acceptance.date * 1000).toLocaleDateString() : 'N/A'} 
                                />
                            </DetailCard>
                        )}

                        {/* Metadata */}
                        {(stripeStatus as any).metadata && Object.keys((stripeStatus as any).metadata).length > 0 && (
                            <DetailCard
                                title="Metadata"
                                icon="fa-solid fa-tags"
                                iconColor="#6610f2"
                            >
                                {Object.entries((stripeStatus as any).metadata).map(([key, value]) => (
                                    <DetailRow key={key} label={key} value={String(value)} />
                                ))}
                            </DetailCard>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

// Helper component for detail rows
const DetailRow = ({ label, value, valueColor }: { label: string; value: string | number | null | undefined; valueColor?: string }) => {
    if (value === null || value === undefined) return null;
    
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '1px solid #e9ecef'
        }}>
            <span style={{ fontWeight: '500', color: '#495057', fontSize: '14px' }}>{label}:</span>
            <span style={{ 
                color: valueColor || '#212529', 
                fontSize: '14px',
                fontWeight: valueColor ? '600' : '400',
                textAlign: 'right',
                maxWidth: '60%',
                wordBreak: 'break-word'
            }}>
                {String(value)}
            </span>
        </div>
    );
};

// Helper component for detail cards
const DetailCard = ({ title, icon, iconColor, children }: { title: string; icon: string; iconColor: string; children: React.ReactNode }) => {
    return (
        <div style={{
            padding: '25px',
            backgroundColor: '#ffffff',
            border: '1px solid #dee2e6',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
        }}
        >
            <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                paddingBottom: '15px',
                borderBottom: '2px solid #e9ecef'
            }}>
                <i className={icon} style={{ color: iconColor, fontSize: '20px' }}></i>
                {title}
            </h3>
            <div>
                {children}
            </div>
        </div>
    );
};

export default Payout;